import "@expo/metro-runtime";
import { registerRootComponent } from "expo";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, SafeAreaView, StyleSheet, Text, TextInput, View } from "react-native";
import "react-native-get-random-values";
import { ExpoRoot } from "./ExpoRoot";
import { claimsFromAuthToken, fetchConfiguration } from "./devConfiguration";

const isAutomated = Boolean(process.env.EXPO_PUBLIC_CI) || process.env.EXPO_PUBLIC_APP_VARIANT === "test";

const fetchConfiguredAuthToken = async (): Promise<string> => {
  const configuration = await fetchConfiguration();

  return typeof configuration.auth_token === "string" ? configuration.auth_token : "";
};

const DevRoot = () => {
  const [isRootMounted, setIsRootMounted] = useState(false);
  const [isConfigurationLoaded, setIsConfigurationLoaded] = useState(false);
  const [authToken, setAuthTokenValue] = useState("");

  useEffect(() => {
    let isCancelled = false;

    fetchConfiguredAuthToken().then((configuredAuthToken) => {
      if (isCancelled) {
        return;
      }

      setAuthTokenValue(configuredAuthToken);
      setIsConfigurationLoaded(true);

      if (isAutomated) {
        setIsRootMounted(true);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, []);

  const claims = useMemo(() => claimsFromAuthToken(authToken), [authToken]);
  const expiresAt = typeof claims.exp === "number" ? new Date(claims.exp * 1000) : undefined;
  const isExpired = expiresAt !== undefined && expiresAt.getTime() < Date.now();

  const getAuthToken = useCallback(() => Promise.resolve(authToken), [authToken]);

  const handleMountRoot = useCallback(() => setIsRootMounted(true), []);

  if (isRootMounted) {
    return <ExpoRoot getAuthToken={getAuthToken} />;
  }

  if (!isConfigurationLoaded) {
    return null;
  }

  return (
    <SafeAreaView style={styles.container}>
      <Text style={styles.title}>Checkout</Text>

      <View style={styles.section}>
        <Text style={styles.label}>Token owner</Text>
        {typeof claims.uuid === "string" ? (
          <>
            <Text style={styles.detail}>{claims.uuid}</Text>
            <Text style={styles.detail}>
              {typeof claims.displayName === "string" ? claims.displayName : "unknown"} ·{" "}
              {typeof claims.tradename === "string" ? claims.tradename : "unknown"}
            </Text>
          </>
        ) : (
          <Text style={styles.expired}>No auth token configured. Paste one to continue.</Text>
        )}
        {expiresAt !== undefined ? (
          <Text style={isExpired ? styles.expired : styles.detail}>
            {isExpired ? "Token expired on " : "Token valid until "}
            {expiresAt.toISOString().slice(0, 10)}
          </Text>
        ) : null}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Auth token</Text>
        <Text style={styles.hint}>
          Comes from auth_token in config.json. Paste a fresh one from the UAF dev cookies to override it.
        </Text>
        <TextInput
          numberOfLines={3}
          placeholder="Enter Auth Token"
          style={styles.input}
          value={authToken}
          multiline
          onChangeText={setAuthTokenValue}
        />
      </View>

      <View style={styles.buttonContainer}>
        <Button disabled={!authToken} testID="go-button" title="GO!" onPress={handleMountRoot} />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    marginVertical: 10,
    width: "80%",
  },
  container: {
    alignItems: "center",
    backgroundColor: "#fff",
    flex: 1,
    justifyContent: "center",
  },
  detail: {
    fontSize: 14,
    marginTop: 4,
  },
  expired: {
    color: "#d0021b",
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 4,
  },
  hint: {
    color: "#808080",
    fontSize: 12,
    marginTop: 4,
  },
  input: {
    borderColor: "gray",
    borderWidth: 1,
    height: 80,
    marginTop: 5,
    paddingHorizontal: 10,
    textAlignVertical: "top",
    width: "100%",
  },
  label: {
    fontSize: 16,
    fontWeight: "bold",
  },
  section: {
    marginBottom: 20,
    width: "80%",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 20,
  },
});

registerRootComponent(DevRoot);
